import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Loader2, AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminNotificationAPI } from '../../services/api';

export default function DeleteNotificationDialog({
  open,
  onOpenChange,
  notification,
  bulkMode = false,
  selectedIds = [],
  onSuccess
}) {
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);

    try {
      if (bulkMode && selectedIds.length > 0) {
        // Bulk delete
        await adminNotificationAPI.bulkAction(selectedIds, 'delete');
        toast.success(`${selectedIds.length} notification(s) deleted successfully`);
      } else if (notification) {
        // Single delete
        await adminNotificationAPI.delete(notification._id);
        toast.success('Notification deleted successfully');
      }

      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete notification(s)';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getDeleteCount = () => {
    if (bulkMode) return selectedIds.length;
    return 1;
  };

  const getTitle = () => {
    if (bulkMode) {
      return `Delete ${selectedIds.length} Notification${selectedIds.length > 1 ? 's' : ''}`;
    }
    return 'Delete Notification';
  };

  const getDescription = () => {
    if (bulkMode) {
      return (
        <>
          Are you sure you want to delete <strong>{selectedIds.length}</strong> selected notification
          {selectedIds.length > 1 ? 's' : ''}? This action cannot be undone.
        </>
      );
    }
    return (
      <>
        Are you sure you want to delete the notification{' '}
        <strong>"{notification?.title}"</strong>? This action cannot be undone.
      </>
    );
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <AlertDialogTitle>{getTitle()}</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="space-y-3">
            <p>{getDescription()}</p>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="flex gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Warning:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>This will permanently remove the notification{getDeleteCount() > 1 ? 's' : ''}</li>
                    <li>Recipients will no longer see {getDeleteCount() > 1 ? 'these notifications' : 'this notification'}</li>
                    <li>This action cannot be undone</li>
                  </ul>
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete {getDeleteCount() > 1 ? `${getDeleteCount()} Notifications` : 'Notification'}
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
